import { LakehouseTableStatusEnum, Uni } from '@vendia/management-api-types'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import debug from 'debug'
import { useParams } from 'react-router-dom'
import Chatbot from 'src/chatbot'
import { SqlChatbot } from 'src/chatbot-sql'
import Button from 'src/components/buttons/button'
import Card from 'src/components/containers/card'
import { ComponentLakehouseTable } from 'src/components/lakehouse/tables/table'
import Loader from 'src/components/loaders/page-loader'
import EmptyState from 'src/components/messages/empty-state'
import PageHeader from 'src/components/page-header'
import StatusPill from 'src/components/pills/status-pill'
import { useDevSettings } from 'src/pages/developer-settings.page'
import useFeatureToggle, { Features } from 'src/utils/hooks/use-feature-toggle'
import { useGetLakehouseConnections } from 'src/utils/hooks/use-get-lakehouse-connections'
import { useGetLakehouseData } from 'src/utils/hooks/use-get-lakehouse-data'
import { useGetUni } from 'src/utils/hooks/use-get-uni'
import { useInterval } from 'src/utils/hooks/use-interval'
import analytics, { EVENTS } from 'src/utils/analytics'

const logger = debug('app:lakehouse-overview')

dayjs.extend(relativeTime)

export const PageLakehouseOverview = () => {
  const { data: uniData } = useGetUni()
  const uni = uniData?.getUni as Uni
  const { node: nodeName } = useParams()

  // Enable AI for certain demo unis
  const { isEnabled: isVinnieEnabled, isLoading: isFeatureFlagLoading } = useFeatureToggle(Features.VINNIE)
  const { devSettings } = useDevSettings()
  const isAiEnabledUni = devSettings?.aiEnabledUnis?.includes(uni?.name) && isVinnieEnabled && !isFeatureFlagLoading
  const isAiWithSqlEnabledUni =
    devSettings?.aiWithSqlEnabledUnis?.includes(uni?.name) && isVinnieEnabled && !isFeatureFlagLoading
  const { data, isLoading: isLoadingTables } = useGetLakehouseData()
  const tables = data?.tables ?? []
  const useGetLakehouseConnectionsResponse = useGetLakehouseConnections()
  const connections = useGetLakehouseConnectionsResponse.data?.connections ?? []

  const isTableCreating = tables.some((table) => table.status === LakehouseTableStatusEnum.Creating)

  // Poll for table creation updates
  useInterval(() => {
    if (isTableCreating) {
      logger('Polling for table updates...')
    }
  }, 30 * 1000)

  const isLakehouseReady = uni?.status === 'RUNNING'

  const statusPill =
    uni?.status && uni.status !== 'RUNNING' ? <StatusPill status={uni.status} data-testid='uni-status' /> : null

  if (!uni) {
    return (
      <div className='grid place-items-center'>
        <Loader />
      </div>
    )
  }

  return (
    <>
      <div className='flex flex-grow flex-col'>
        <PageHeader
          title={'Tables'}
          description='Ingest tables, then filter and join to share data products'
          titleAfter={statusPill}
          actions={
            <div className='flex gap-3'>
              <Button
                kind='secondary'
                icon='sidenav-settings-gear'
                iconSize={14}
                to='../data/connections'
                disabled={!isLakehouseReady}
              >
                Connections
              </Button>
              {tables.length ? (
                <Button
                  icon='plus-m'
                  iconSize={14}
                  to='../data/add-table'
                  disabled={!isLakehouseReady}
                  onClick={() => {
                    analytics.track(EVENTS.TABLE_ADD_CLICKED, {
                      uniName: uni?.name,
                      nodeName,
                    })
                  }}
                >
                  Table
                </Button>
              ) : null}
            </div>
          }
          testid='uni-name-header'
        />
        <div className='flex p-6 px-12'>
          {isLoadingTables ? (
            <Loader />
          ) : tables.length ? (
            <div className='flex w-full flex-col'>
              <ComponentLakehouseTable uni={uni} nodeName={nodeName!} connections={connections} />
            </div>
          ) : (
            <Card isTable>
              <EmptyState
                title='Create tables'
                description='Import tables from multiple sources to securely collaborate, share, and generate insights across data platforms.'
                imageSource='/images/video-bg.webp'
                youtube='QDGMoPdMJvU'
                actions={
                  <>
                    <Button
                      to={'../data/add-table'}
                      disabled={!isLakehouseReady}
                      onClick={() => {
                        analytics.track(EVENTS.TABLE_ADD_CLICKED, {
                          uniName: uni?.name,
                          nodeName,
                        })
                      }}
                    >
                      Create table
                    </Button>
                    <Button
                      kind='secondary'
                      href={'https://docs.vendia.com/platform/analytical/overview/'}
                      target='_blank'
                    >
                      Documentation
                    </Button>
                  </>
                }
              />
            </Card>
          )}
        </div>
      </div>

      {isAiEnabledUni ? <Chatbot /> : null}
      {isAiWithSqlEnabledUni ? <SqlChatbot /> : null}
    </>
  )
}
